import { z } from 'zod';

import { baseEntitySchema } from './baseContract';
import { urlSchema } from './schemaUtils';
import {
  ContentType,
  DocumentStatus,
  SourceType,
  UpdateFrequency,
} from '../enums';

export const baseDocumentSchema = baseEntitySchema.extend({
  type: z.nativeEnum(ContentType),
  /**
   * Where the document came from. Orthogonal to `type` (how we parse it).
   * Optional on the wire: the entity defaults to `upload`, so create payloads
   * need not carry it.
   */
  sourceType: z.nativeEnum(SourceType).optional(),
  name: z.string(),
  uri: z.string(),
  publicUrl: z.string(),
  status: z.nativeEnum(DocumentStatus),
  content: z.string().nullish(),
  metadata: z.record(z.any()).nullish(),
  parsingMetadata: z.record(z.any()).nullish(),
  embeddingsCount: z.number().nullish(),
  scheduledAt: z.string().datetime().nullish(),
  startedParsingAt: z.string().datetime().nullish(),
  finishedParsingAt: z.string().datetime().nullish(),
  startedEmbeddingAt: z.string().datetime().nullish(),
  finishedEmbeddingAt: z.string().datetime().nullish(),
  availableForAllAgents: z.boolean(),
});

export const documentSchema = baseDocumentSchema.extend({
  name: z.string().optional(),
  type: z.nativeEnum(ContentType),
  chats: z.array(z.string().uuid()),
  folderId: z.string().uuid().nullable().optional(),
});

export const createDocumentSchema = documentSchema
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .strict();

export const createWebsiteDocumentSchema = z
  .object({
    urls: z.array(z.string()),
    updateFrequency: z.nativeEnum(UpdateFrequency).nullable().optional(),
    chats: z.array(z.string().uuid()),
    weights: z.record(z.number()),
    availableForAllAgents: z.boolean(),
    folderId: z.string().uuid().nullable().optional(),
  })
  .strict();

export const createDocumentDocumentSchema = z.object({
  chats: z.array(z.string().uuid()),
  fileName: z.string(),
  contentType: z.string(),
  size: z.number(),
  availableForAllAgents: z.boolean(),
  folderId: z.string().uuid().nullable().optional(),
});

export const createExternalDocumentSchema = z.object({
  chats: z.array(z.string().uuid()),
  availableForAllAgents: z.boolean(),
  uri: z.string().url(),
  name: z.string().optional(),
  updateFrequency: z.nativeEnum(UpdateFrequency).nullable().optional(),
  folderId: z.string().uuid().nullable().optional(),
});

export const updateDocumentSchema = documentSchema
  .partial()
  .required({
    id: true,
  })
  .strict();

export const mapUrlsSchema = z.object({
  url: z.string(),
});

/**
 * Add-source modal's "Online sources" tab (master §3.4, Phase K4b): classifies
 * a URL so the UI can route to the right existing sub-flow (website crawl /
 * external document / RSS feed) without asking the user to pre-classify it.
 */
export const classifyUrlSchema = z.object({
  url: urlSchema,
});

export const classifyUrlKindSchema = z.enum([
  'website',
  'document',
  'rss',
  'google_drive',
]);

export const classifyUrlResultSchema = z.object({
  kind: classifyUrlKindSchema,
  url: z.string(),
});

export const findAllEmbeddingsDocumentSchema = z.object({
  documentId: z.string().uuid(),
});

export const refreshAllDocumentsSchema = z.object({
  chatId: z.string().uuid(),
  statuses: z.array(z.nativeEnum(DocumentStatus)).optional(),
});

export const updateDocumentAutoUpdateSchema = z.object({
  acceptCookiesSelector: z.string().trim().max(500).nullable().optional(),
  id: z.string().uuid(),
  updateFrequency: z.nativeEnum(UpdateFrequency).nullable(),
  changeThreshold: z.number().min(0).max(1).nullable(),
});

export const bulkMoveToFolderSchema = z.object({
  documentIds: z.array(z.string().uuid()).min(1),
  folderId: z.string().uuid().nullable(),
});

export const bulkDeleteDocumentsSchema = z.object({
  documentIds: z.array(z.string().uuid()).min(1),
});

export const bulkAssignToChatsSchema = z.object({
  documentIds: z.array(z.string().uuid()).min(1),
  chatIds: z.array(z.string().uuid()),
  availableForAllAgents: z.boolean().optional(),
});

export const bulkUpdateDocumentAutoUpdateSchema = z
  .object({
    documentIds: z.array(z.string().uuid()).min(1),
    updateFrequency: z.nativeEnum(UpdateFrequency).nullable().optional(),
    changeThreshold: z.number().min(0).max(1).nullable().optional(),
  })
  .refine(
    data =>
      data.updateFrequency !== undefined || data.changeThreshold !== undefined,
    {
      message: 'At least one field must be provided for update',
    },
  );

export const bulkRefreshDocumentsSchema = z.object({
  documentIds: z.array(z.string().uuid()).min(1),
});

export const bulkUpdateWeightSchema = z.object({
  documentIds: z.array(z.string().uuid()).min(1),
  weight: z.number().min(0).max(1),
});

export type MapUrlsDTO = z.infer<typeof mapUrlsSchema>;
export type ClassifyUrlDTO = z.infer<typeof classifyUrlSchema>;
export type ClassifyUrlKind = z.infer<typeof classifyUrlKindSchema>;
export type ClassifyUrlResult = z.infer<typeof classifyUrlResultSchema>;
export type DocumentDTO = z.infer<typeof documentSchema>;
export type CreateDocumentDTO = z.infer<typeof createDocumentSchema>;
export type CreateWebsiteDocumentDTO = z.infer<
  typeof createWebsiteDocumentSchema
>;
export type CreateExternalDocumentDTO = z.infer<
  typeof createExternalDocumentSchema
>;
export type UpdateDocumentDTO = z.infer<typeof updateDocumentSchema>;
export type FindAllEmbeddingsDocumentDTO = z.infer<
  typeof findAllEmbeddingsDocumentSchema
>;
export type RefreshAllDocumentsDTO = z.infer<typeof refreshAllDocumentsSchema>;
export type UpdateDocumentAutoUpdateDTO = z.infer<
  typeof updateDocumentAutoUpdateSchema
>;
export type BulkMoveToFolderDTO = z.infer<typeof bulkMoveToFolderSchema>;
export type BulkDeleteDocumentsDTO = z.infer<typeof bulkDeleteDocumentsSchema>;
export type BulkAssignToChatsDTO = z.infer<typeof bulkAssignToChatsSchema>;
export type BulkUpdateDocumentAutoUpdateDTO = z.infer<
  typeof bulkUpdateDocumentAutoUpdateSchema
>;
export type BulkRefreshDocumentsDTO = z.infer<
  typeof bulkRefreshDocumentsSchema
>;
export type BulkUpdateWeightDTO = z.infer<typeof bulkUpdateWeightSchema>;
